import React, {Component} from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import RemoteServices from '~/constants/RemoteServices';
import designIcons from '~/constants/DesignIconConstants';
import {roundDecimal} from '~/shared/utils/math';
import {isSameColor} from '~/utils/colors';
import {getClipartRecolorHexString} from '~/utils/manipulateLambda';
import {buildUrl} from '~/utils/url';

import './index.scss';

function getColorsToBeRecolored(colors) {
  return colors
    .filter(({original, user}) => !isSameColor(original, user))
    .map(({original, user}) => ({
      from: getClipartRecolorHexString(original),
      to: getClipartRecolorHexString(user)
    }));
}

export function buildImageUrl(icon, scale = 1) {
  if (!icon) return null;

  const height = icon.height;
  const width = icon.width;

  const params = {
    height: Math.min(
      roundDecimal(height * scale, 0),
      designIcons.DOWNLOAD.MAX_HEIGHT
    ),
    width: Math.min(
      roundDecimal(width * scale, 0),
      designIcons.DOWNLOAD.MAX_WIDTH
    ),
    red: icon.color?.r,
    green: icon.color?.g,
    blue: icon.color?.b,
    clipart_id: icon.clipartId,
    path: icon.path,
    fV: icon.verticalFlip,
    fH: icon.horizontalFlip,
    lockRatio: icon.lockRatio,
    grayscale: icon.singleColor,
    blackwhite: false,
    rotate: icon.rotation,
    tag_id: icon.categoryTagId,
    transparent: !!icon.singleColor
  };

  if (!icon.singleColor) {
    getColorsToBeRecolored(icon.colors).forEach(
      ({from, to}) => (params[`recolor[${from}]`] = to)
    );
  }
  return buildUrl(`${RemoteServices.manipulate}/manipulate`, params);
}

const propTypes = {
  options: PropTypes.object.isRequired,
  handleError: PropTypes.func,
  scale: PropTypes.number,
  onRender: PropTypes.func
};
/** @extends {Component<PropTypes.InferProps<typeof propTypes>>} */
export default class ClipartImage extends Component {
  static displayName = 'ClipartImage';
  static propTypes = propTypes;

  static defaultProps = {
    handleError: function () {},
    onRender: function () {}
  };

  /** @param {PropTypes.InferProps<typeof propTypes>} nextProps - Component properties. */
  shouldComponentUpdate(nextProps) {
    return (
      this.props.scale !== nextProps.scale ||
      !isEqual(this.props.options, nextProps.options) ||
      this.props.handleError !== nextProps.handleError ||
      this.props.onRender !== nextProps.onRender
    );
  }

  handleDomNodeUpdate = node => {
    if (this.image !== node) {
      if (this.image) {
        this.image.removeEventListener('load', this.onLoad);
      }
      if (node) {
        this.onLoad = event => {
          const {naturalWidth, naturalHeight} = event.target;
          this.props.onRender({
            naturalWidth,
            naturalHeight,
            rotation: this.props.options.rotation
          });
        };
        node.addEventListener('load', this.onLoad);
      }
      this.image = node;
    }
  };

  render() {
    return (
      <img
        ref={this.handleDomNodeUpdate}
        className="ndx-ClipartImage"
        draggable={false}
        onError={this.props.handleError}
        src={buildImageUrl(this.props.options, this.props.scale)}
      />
    );
  }
}
